import { useState } from 'react';
import { Copy, Check, Eye, EyeOff, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEntitlementContext } from '@/contexts/EntitlementContext';
import { useGenerateScimToken, useRevokeScimToken } from '@/hooks/useIdentityProviders';

interface ScimConfigPanelProps {
  idpId: string;
}

// The SCIM endpoint must point at the API host, not the app host. In production
// the app is served from app.gateco.ai but the API (and /api/scim/v2) lives at
// api.gateco.ai via VITE_API_URL. Using window.location.origin here would show a
// broken URL that returns the SPA shell instead of SCIM. Mirrors API_BASE in
// api/client.ts. Falls back to the current origin in dev (Vite proxies /api).
const SCIM_API_ORIGIN = import.meta.env.VITE_API_URL || window.location.origin;
const SCIM_ENDPOINT = `${SCIM_API_ORIGIN}/api/scim/v2`;

/** Copies text to clipboard and briefly shows a checkmark. */
function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      className="h-7 w-7 p-0"
      aria-label="Copy to clipboard"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-status-allow" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

/** Token display with show/hide toggle and copy button. */
function TokenDisplay({ token }: { token: string }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="space-y-1">
      <div className="rounded-md border bg-muted/50 px-3 py-2 flex items-center justify-between gap-2">
        <span className="font-mono text-xs break-all select-all flex-1">
          {visible ? token : '\u2022'.repeat(Math.min(token.length, 40))}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setVisible((v) => !v)}
            className="h-7 w-7 p-0"
            aria-label={visible ? 'Hide token' : 'Show token'}
          >
            {visible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </Button>
          <CopyButton value={token} />
        </div>
      </div>
      <div className="flex items-start gap-1.5 text-xs text-status-warning">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>This token will not be shown again. Copy it now and store it securely.</span>
      </div>
    </div>
  );
}

/**
 * SCIM configuration panel for a connected identity provider.
 * Displays the SCIM v2 endpoint URL and provides token management
 * for Growth and Enterprise plans. Lower plans see a disabled/gated state.
 */
export function ScimConfigPanel({ idpId }: ScimConfigPanelProps) {
  const { hasFeature, needsUpgrade } = useEntitlementContext();
  const hasScim = hasFeature('sso_scim');

  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [hasActiveToken, setHasActiveToken] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateMutation = useGenerateScimToken();
  const revokeMutation = useRevokeScimToken();

  function handleGenerate() {
    setError(null);
    generateMutation.mutate(idpId, {
      onSuccess: (res) => {
        setGeneratedToken(res.token);
        setHasActiveToken(true);
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : 'Failed to generate SCIM token');
      },
    });
  }

  function handleRevoke() {
    setError(null);
    revokeMutation.mutate(idpId, {
      onSuccess: () => {
        setGeneratedToken(null);
        setHasActiveToken(false);
        setConfirmRevoke(false);
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : 'Failed to revoke SCIM token');
        setConfirmRevoke(false);
      },
    });
  }

  return (
    <div className="space-y-3 border-t pt-4 mt-2">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        SCIM Provisioning
      </h4>

      {/* Endpoint URL — always visible */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">SCIM v2 Endpoint</label>
        <div className="rounded-md border bg-muted/50 px-3 py-2 flex items-center justify-between gap-2">
          <span className="font-mono text-xs break-all select-all flex-1">{SCIM_ENDPOINT}</span>
          <CopyButton value={SCIM_ENDPOINT} />
        </div>
      </div>

      {/* Token management — gated to Growth and above (sso_scim) */}
      {!hasScim ? (
        <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground space-y-1">
          <p className="font-medium text-foreground">Bearer Token</p>
          <p>
            Token generation requires the Growth plan or above.{' '}
            {needsUpgrade('sso_scim') && (
              <span className="text-primary font-medium">Upgrade to Growth to enable SCIM push provisioning.</span>
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">Bearer Token</label>

          {error && (
            <div className="text-xs text-destructive bg-destructive/10 rounded px-2 py-1.5 flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}

          {generatedToken ? (
            <TokenDisplay token={generatedToken} />
          ) : (
            <p className="text-xs text-muted-foreground">
              {hasActiveToken
                ? 'A token is currently active. Generate a new one to rotate it.'
                : 'No token issued yet. Generate a bearer token to configure your identity provider.'}
            </p>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerate}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {hasActiveToken ? 'Rotate Token' : 'Generate Token'}
            </Button>

            {hasActiveToken && (
              <>
                {confirmRevoke ? (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleRevoke}
                    disabled={revokeMutation.isPending}
                  >
                    {revokeMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                    Confirm Revoke
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmRevoke(true)}
                  >
                    Revoke Token
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
